import React from "react";
import {
  FormControl,
  FormControlLabel,
  Input,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SubActionButton from "@quest_editor/components/Buttons/SubActionButton";
import InfoSection from "@quest_editor/components/InfoSection";
import { IncentiveType, IncentiveTypeValue } from "@quest_editor/constants";
import ImagePreviewBox from "./ImagePreviewBox";

const BasicInfoSection = ({
  name,
  setName,
  description,
  setDescription,
  note,
  setNote,
  incentiveType,
  onIncentiveTypeChange,
  tamaruPoint,
  setTamaruPoint,
  prizeName,
  setPrizeName,
  imageSelection,
  setImageSelection,
  fileName,
  previewUrl,
  onFileChange,
  fileInputRef,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
}: {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  incentiveType: IncentiveTypeValue;
  onIncentiveTypeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  tamaruPoint: string;
  setTamaruPoint: (v: string) => void;
  prizeName: string;
  setPrizeName: (v: string) => void;
  imageSelection: string;
  setImageSelection: (v: string) => void;
  fileName: string;
  previewUrl: string | null;
  onFileChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  startDate: string;
  setStartDate: (v: string) => void;
  startTime: string;
  setStartTime: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  endTime: string;
}) => {
  const { t } = useTranslation("questEditor");

  return (
    <InfoSection
      title={t("quest_edit.basic_info.title")}
      rows={[
        {
          label: t("quest_edit.basic_info.form.name.label"),
          required: true,
          children: (
            <TextField
              size="small"
              placeholder={t("quest_edit.basic_info.form.name.placeholder")}
              sx={{ width: "50%" }}
              value={name}
              onChange={(e) => setName(e.target.value)}
            ></TextField>
          ),
        },
        {
          label: t("quest_edit.basic_info.form.description.label"),
          children: (
            <TextField
              size="small"
              multiline
              rows={4}
              placeholder={t(
                "quest_edit.basic_info.form.description.placeholder",
              )}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            ></TextField>
          ),
        },
        {
          label: t("quest_edit.basic_info.form.label.label"),
          children: (
            <>
              <TextField
                size="small"
                placeholder={t("quest_edit.basic_info.form.label.placeholder")}
                sx={{ width: "50%" }}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              ></TextField>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                {t("quest_edit.basic_info.form.label.note")}
              </Typography>
            </>
          ),
        },
        {
          label: t("quest_edit.basic_info.form.reward.label"),
          required: true,
          children: (
            <RadioGroup
              value={incentiveType === IncentiveType.TAMARU ? "1" : "2"}
              onChange={onIncentiveTypeChange}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label={t("quest_edit.basic_info.form.reward.tamaru")}
                  sx={{ width: "10%", color: "text.secondary" }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  sx={{ width: "25%" }}
                  type="number"
                  value={tamaruPoint}
                  onChange={(e) => setTamaruPoint(e.target.value)}
                  disabled={incentiveType !== IncentiveType.TAMARU}
                />
                <Typography fontWeight="bold" sx={{ color: "text.secondary" }}>
                  {t("quest_edit.basic_info.form.reward.tamaru_unit")}
                </Typography>
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  value="2"
                  control={<Radio />}
                  label={t("quest_edit.basic_info.form.reward.prize")}
                  sx={{ width: "10%", color: "text.secondary" }}
                />
                <TextField
                  size="small"
                  variant="outlined"
                  sx={{ width: "40%" }}
                  value={prizeName}
                  onChange={(e) => setPrizeName(e.target.value)}
                  disabled={incentiveType !== IncentiveType.PRIZE}
                />
              </Stack>
            </RadioGroup>
          ),
        },
        {
          label: t("quest_edit.basic_info.form.image.label"),
          required: true,
          children: (
            <Stack spacing={2}>
              <Stack direction="row" spacing={4} alignItems="flex-start">
                <Stack spacing={2} sx={{ flexGrow: 1, width: "60%" }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <FormControl size="small" sx={{ width: "50%" }}>
                      <Select
                        value={imageSelection}
                        onChange={(e) => setImageSelection(e.target.value)}
                        sx={{ width: "80%" }}
                      >
                        <MenuItem value="upload">
                          {t("quest_edit.basic_info.form.image.option.type1")}
                        </MenuItem>
                        <MenuItem value="default">
                          {t("quest_edit.basic_info.form.image.option.type2")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      value={fileName}
                      placeholder={t(
                        "quest_edit.basic_info.form.image.placeholder",
                      )}
                      disabled={imageSelection !== "upload"}
                      sx={{ width: "40%" }}
                      InputProps={{ readOnly: true }}
                    />
                    <SubActionButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageSelection !== "upload"}
                      sx={{ minWidth: 40, p: 0 }}
                    >
                      {t("quest_edit.button.add_img")}
                    </SubActionButton>
                    <Input
                      type="file"
                      sx={{ display: "none" }}
                      inputRef={fileInputRef}
                      onChange={onFileChange}
                      inputProps={{ accept: "image/*" }}
                    />
                  </Stack>
                </Stack>

                <ImagePreviewBox previewUrl={previewUrl} alt={name} />
              </Stack>

              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                {t("quest_edit.basic_info.form.image.note")}
              </Typography>
            </Stack>
          ),
        },
        {
          label: t("quest_edit.basic_info.form.period.label"),
          required: true,
          children: (
            <Stack direction="row" alignItems="center" spacing={1}>
              <TextField
                type="date"
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <Typography color="text.primary">
                {t("quest_edit.basic_info.form.period.through")}
              </Typography>

              <TextField
                type="date"
                size="small"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="time"
                size="small"
                value={endTime}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Stack>
          ),
        },
      ]}
    />
  );
};

export default BasicInfoSection;
